import React, { useEffect, useState } from 'react';
import { useAuthState } from '@vis-auth/react';
import { getConfiguration, ResourcesService } from '@save/utils';
import CollapsableCard from './collapsable-card.component';
import { CollapsableCardProvider } from './collapsable-card-context';
import DOMPurify from 'dompurify';

export interface MessageCenterProps {
  count?: number;
}

interface Message {
  title: string;
  lastModifiedDate: Date;
  content: string;
}

const DATE_FORMAT_OPTIONS = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
};

const createMessageObjects = (message: any): Message =>
  ({
    title: message.title,
    lastModifiedDate: new Date(message.last_modified_date),
    content: DOMPurify.sanitize(message.content, { USE_PROFILES: { html: true } }),
  }) as Message;

const sortMessagesNewestFirst = (m1, m2): number => m2.lastModifiedDate - m1.lastModifiedDate;

export default function Messages({ count = 0 }: MessageCenterProps): JSX.Element {
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState<boolean>(false);
  const { accessToken } = useAuthState();

  useEffect(() => {
    const fetchMessages = async () => {
      const resourceService = new ResourcesService(getConfiguration(accessToken));

      await resourceService
        .getMessages()
        .then((data) => {
          const sortedMessages = data.map(createMessageObjects).sort(sortMessagesNewestFirst);

          setMessages(count ? sortedMessages.slice(0, count) : sortedMessages);
        })
        .catch((err) => {
          setError(true);
        });
    };
    fetchMessages();
  }, [accessToken, count]);

  if (error) {
    throw new Error('Error retrieving messages');
  }

  return (
    <CollapsableCardProvider>
      {messages.map((message, i) => (
        <CollapsableCard
          header={message.title}
          id={`message-${i}`}
          subheader={`Last Modified ${message.lastModifiedDate.toLocaleDateString('en-US', DATE_FORMAT_OPTIONS)}`}
          key={i}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: message.content,
            }}
          />
        </CollapsableCard>
      ))}
    </CollapsableCardProvider>
  );
}
